// src/PasswordReset/PasswordReset.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { userService } from '../services';
import Button from '../components/buttons/Button';
import { isCapacitor } from '../utils/platform';
import './PasswordReset.css';
import { FaRecycle } from 'react-icons/fa';

function PasswordReset() {
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get token from either path parameter or query parameter
  const token = pathToken || searchParams.get('token');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const result = await userService.validateResetToken(token);
      setIsValidToken(result.valid);
    } catch (err) {
      setIsValidToken(false);
      setError("Ce lien de réinitialisation est invalide ou a expiré.");
    } finally {
      setIsValidating(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return false;
    }
    
    if (formData.password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères");
      return false;
    }
    
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
    if (!passwordRegex.test(formData.password)) {
      setError("Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);

    try {
      await userService.resetPassword(token, formData.password);

      setIsSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } catch (err) {
      if (err.message?.includes('invalid') || err.message?.includes('expired')) {
        setError("Ce lien de réinitialisation est invalide ou a expiré.");
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(`Une erreur est survenue: ${err.message || 'Erreur inconnue'}. Veuillez réessayer.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-loading">
          <div className="password-reset-loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-container">
      <div className="password-reset-wrapper">
        {!isCapacitor && (
          <div className="password-reset-header">
            <img src="icon.png" alt="Logo" className="password-reset-logo" />
            <h2 className="password-reset-title">voxia</h2>
          </div>
        )}
        
        <div className="password-reset-content">
          <h3 className="password-reset-form-title">Réinitialiser le mot de passe</h3>
          
          {!isValidToken ? (
            <div className="password-reset-error-container">
              <p className="password-reset-error-message">{error}</p>
              <Link to="/forgot-password" className="password-reset-link">
                Demander un nouveau lien
              </Link>
            </div>
          ) : isSuccess ? (
            <div className="password-reset-success-container">
              <p className="password-reset-success-message">
                Votre mot de passe a été réinitialisé avec succès!
              </p>
              <p className="password-reset-info-message">
                Vous allez être redirigé vers la page de connexion...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="password-reset-form">
              <div className={`password-reset-message ${error ? 'error' : ''}`}>
                {error || "Choisissez un nouveau mot de passe pour votre compte"}
              </div>
              
              <div className="password-reset-form-group">
                <label htmlFor="password" className="password-reset-label">
                  Nouveau mot de passe
                </label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  className="password-reset-input"
                  placeholder="Minimum 8 caractères"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                />
              </div>
              
              <div className="password-reset-form-group">
                <label htmlFor="confirmPassword" className="password-reset-label">
                  Confirmer le mot de passe
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  name="confirmPassword"
                  className="password-reset-input"
                  placeholder="Retapez votre mot de passe"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required
                />
              </div>
              
              <Button
                variant="primary" 
                disabled={isLoading}
                icon={FaRecycle}
              >
                {isLoading ? 'Réinitialisation...' : 'Réinitialiser'}
              </Button>
            </form>
          )}
          
          <div className="password-reset-links">
            <Link to="/auth" className="password-reset-link">
              Retour à la connexion
            </Link>
          </div>
        </div>
        
        {!isCapacitor && (
          <div className="password-reset-footer">
            <a 
              href="http://www.swissmadesoftware.org" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="password-reset-swissmade-link"
            >
              <img src="swissmade.png" alt="Swiss Made Software" className="password-reset-swissmade-logo" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default PasswordReset;